import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgModule, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PreviewService } from '../../../core/preview.service';
import { ExpansionPanelModule } from '../../../expansion-panel/expansion-panel.module';

import { FeatureDetectionService } from '../../../core/feature-detection.service';
import { LazyContentGenericComponent } from '../../models/lazy-content.models';

@Component({
    selector: 'ncg-preview-overlay',
    template: `
        <aside aria-label="See info regarding the preview session" class="preview-overlay" *ngIf="states.isVisible">
            <ncg-expansion-panel>
                <ng-container expansion-title>Preview mode</ng-container>
                <ng-container expansion-icon>
                    <svg-icon-sprite
                        src="chevron-up"
                        [viewBox]="'0 0 30 30'"
                        [width]="'30px'"
                        [height]="'30px'"
                        aria-hidden="true"
                        class="is-flex"
                    ></svg-icon-sprite>
                </ng-container>
                <div class="preview-overlay__extra">
                    <div class="preview-overlay__share">
                        <div *ngIf="!states.isIframe">
                            <p>You are currently previewing this page.</p>
                            <p *ngIf="published">
                                This page is published in an earlier version:
                                <a *ngIf="publishedUrl" target="_blank" [href]="publishedUrl">Open in new tab</a>
                            </p>
                            <p *ngIf="!published">This page is NOT published yet.</p>
                        </div>
                        <div class="field" *ngIf="states.isIframe">
                            <div class="field has-addons">
                                <div class="control is-expanded">
                                    <input #previewLinkInput class="input" type="text" readonly [value]="url" (click)="onCopyLink()" />
                                </div>
                                <div class="control">
                                    <button type="button" class="button is-primary" title="Copy shareable link" (click)="onCopyLink()">
                                        <svg-icon-sprite
                                            [src]="'map-icon'"
                                            [viewBox]="'0 0 30 30'"
                                            [width]="'30px'"
                                            [height]="'30px'"
                                            aria-hidden="true"
                                            classes=""
                                            class="icon"
                                        ></svg-icon-sprite>
                                    </button>
                                </div>
                            </div>
                            <p class="help">Copy link above to share with others</p>
                        </div>
                    </div>
                    <div class="buttons">
                        <a *ngIf="!states.isIframe" class="button is-primary" href="/" title="Click to navigate to the frontpage">Stop preview</a>
                    </div>
                </div>
            </ncg-expansion-panel>
        </aside>
    `,
    styleUrls: ['./preview-overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewOverlayComponent implements OnInit, OnDestroy, LazyContentGenericComponent {
    private readonly unsubscribe = new Subject<void>();

    states = {
        isVisible: false,
        isIframe: false,
    };
    url: string;
    published: boolean;
    publishedUrl?: string;

    @ViewChild('previewLinkInput', { static: false }) private readonly previewLinkInput: ElementRef<HTMLInputElement>;

    constructor(
        private readonly preview: PreviewService,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.preview
            .getPreviewPageContext()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((ctx) => {
                this.states.isVisible = !!ctx;

                if (ctx) {
                    this.published = ctx.published;
                    this.publishedUrl = this.published ? ctx.url : undefined;
                }

                if (this.featureDetectionService.isBrowser()) {
                    this.url = location.href.replace('&amp;', '&');
                }

                this.cdr.markForCheck();
            });

        if (this.featureDetectionService.isBrowser()) {
            this.states.isIframe = this.inIframe();
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    onCopyLink(): void {
        if (!this.previewLinkInput) {
            return;
        }

        this.previewLinkInput.nativeElement.select();
        document.execCommand('copy');
    }

    private inIframe() {
        try {
            return window.self !== window.top;
        } catch (e) {
            return true;
        }
    }
}
@NgModule({
    imports: [CommonModule, ExpansionPanelModule, IconSpriteModule],
    declarations: [PreviewOverlayComponent],
})
export class PreviewComponentModule {}
